import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  TextField,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";

import { addOneYearIso, toNullableString } from "../../utils/qualifyingScoreUtils";

const useStyles = makeStyles({
  wrapper: {
    minWidth: "400px",
    "@media (max-width: 600px)": { minWidth: "280px" },
  },
  row: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "12px",
    marginTop: "12px",
  },
  note: {
    fontSize: "12px",
    color: "#667085",
    marginTop: "8px",
  },
  actions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "8px",
    marginTop: "16px",
  },
});

// `selectedRows` is the array of full DTO rows the user checked.
// If exactly one (fromDate, toDate) is shared, prefill source dates + suggest source+1y target.
const summarizeSourceRange = (selectedRows) => {
  const pairs = new Set(
    (selectedRows || [])
      .filter((r) => r?.fromDate && r?.toDate)
      .map((r) => `${r.fromDate}|${r.toDate}`),
  );
  if (pairs.size === 1) {
    const [from, to] = Array.from(pairs)[0].split("|");
    return { from, to, mixed: false };
  }
  return { from: "", to: "", mixed: pairs.size > 1 };
};

const QualifyingScoreCloneDialog = ({
  open,
  selectedRows,
  governingBodyName,
  isSubmitting,
  onCancel,
  onSubmit,
}) => {
  const classes = useStyles();
  const summary = useMemo(() => summarizeSourceRange(selectedRows), [selectedRows]);
  const [targetFromDate, setTargetFromDate] = useState("");
  const [targetToDate, setTargetToDate] = useState("");
  const [overwriteExisting, setOverwriteExisting] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!open) return;
    if (summary.mixed) {
      setTargetFromDate("");
      setTargetToDate("");
    } else if (summary.from && summary.to) {
      setTargetFromDate(addOneYearIso(summary.from));
      setTargetToDate(addOneYearIso(summary.to));
    } else {
      setTargetFromDate("");
      setTargetToDate("");
    }
    setOverwriteExisting(false);
    setErrors({});
  }, [open, summary.from, summary.to, summary.mixed]);

  const handleSubmit = () => {
    const next = {};
    const from = toNullableString(targetFromDate);
    const to = toNullableString(targetToDate);
    if (!from) next.targetFromDate = "Required";
    if (!to) next.targetToDate = "Required";
    if (from && to && from > to) next.targetToDate = "Must be on or after target From";
    if (Object.keys(next).length) {
      setErrors(next);
      return;
    }
    setErrors({});
    onSubmit({
      targetFromDate: from,
      targetToDate: to,
      overwriteExisting,
    });
  };

  const count = (selectedRows || []).length;

  return open ? (
    <Box className={classes.wrapper}>
      <Typography variant="body1">
        Copying <b>{count}</b> qualifying score{count === 1 ? "" : "s"}
        {governingBodyName ? ` for ${governingBodyName}` : ""}.
      </Typography>

      {!summary.mixed && summary.from && summary.to ? (
        <Typography className={classes.note}>
          Source dates: {summary.from} → {summary.to}
        </Typography>
      ) : summary.mixed ? (
        <Typography className={classes.note}>
          Selected rows span multiple date ranges — new dates apply to every copy.
        </Typography>
      ) : null}

      <Typography variant="subtitle2" style={{ marginTop: 12 }}>
        Target dates
      </Typography>
      <Box className={classes.row}>
        <TextField
          label="From"
          type="date"
          variant="outlined"
          size="small"
          value={targetFromDate}
          onChange={(e) => setTargetFromDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          error={Boolean(errors.targetFromDate)}
          helperText={errors.targetFromDate}
        />
        <TextField
          label="To"
          type="date"
          variant="outlined"
          size="small"
          value={targetToDate}
          onChange={(e) => setTargetToDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          error={Boolean(errors.targetToDate)}
          helperText={errors.targetToDate}
        />
      </Box>

      <FormControlLabel
        control={
          <Checkbox
            checked={overwriteExisting}
            onChange={(e) => setOverwriteExisting(e.target.checked)}
            size="small"
          />
        }
        label="Overwrite existing rows at target dates"
        style={{ marginTop: 8 }}
      />

      <Box className={classes.actions}>
        <Button onClick={onCancel} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          color="primary"
          variant="contained"
          onClick={handleSubmit}
          disabled={isSubmitting || count === 0}
        >
          Copy
        </Button>
      </Box>
    </Box>
  ) : null;
};

QualifyingScoreCloneDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  selectedRows: PropTypes.array,
  governingBodyName: PropTypes.string,
  isSubmitting: PropTypes.bool,
  onCancel: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
};

export default QualifyingScoreCloneDialog;
